import Head from "next/head";
import { useEffect, useState } from "react";

import { MainHome } from "../components/Home/MainHome";
import LoaderComponent from "../components/Loader";
import { useStoreContext } from "../contexts/StoreDataContext";
import { ogImage } from "../lib/media";
import styles from "../styles/Home.module.css";
import { trackEvent } from "../utils/analytics";
import { EVENTS } from "../utils/analyticsConfig";
import NotFound from "./404";
import Error500Page from "./500";

export default function Home() {
  const context = useStoreContext();
  const store = context?.storeData;
  const theme = context?.themeData;
  const error = context?.error;
  const storeUrl = context?.storeUrl;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (store !== null) {
      setLoading(false);
    }
  }, [store]);

  // Top of the buyer funnel. Keyed on the store id so a re-render does not
  // re-emit, and guarded on the store having actually resolved — firing while
  // `store` is still null would count a load that never showed anything.
  const storeId = store?.id ?? store?._id;
  useEffect(() => {
    if (!storeId) return;
    trackEvent(EVENTS.STORE_VIEWED, {
      store_has_home_page: Boolean(store?.homePageEnabled),
      store_product_count: Array.isArray(store?.products) ? store.products.length : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  if (error && error.code === 500) return <Error500Page />;
  if (error && error === 404) return <NotFound />;
  if (!store || loading) return <LoaderComponent />;

  // below is the old code for the using pageProps when we were using getServerSideProps
  // const { storeData: store, storeUrl, error, themeData: theme } = props;

  // if (error && error.code === 500) return <Error500Page />;
  // if (error && error === 404) return <NotFound />;
  // if (!store) return <LoaderComponent />;

  return (
    <div className={styles.container}>
      <Head>
        <title>{store?.storeName || "DM2Buy Store"}</title>
        <meta name="title" content={store?.storeName || "DM2Buy Store"} />
        {store?.facebookDomainVerificationId ? (
          <meta name="facebook-domain-verification" content={store?.facebookDomainVerificationId} />
        ) : (
          ""
        )}
        <meta name="description" content={store?.storeBio || "Online store powered by DM2Buy"} />
        <link rel="icon" href="https://dm2buy.blob.core.windows.net/dm2buy/favicon.ico" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@700;900&family=Roboto:wght@400;700&display=swap"
          rel="stylesheet"
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1,user-scalable=0"
        />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={store?.storeName || "DM2Buy Store"} />
        <meta
          property="og:description"
          content={store?.storeBio || "Online store powered by DM2Buy"}
        />
        <meta
          property="og:image"
          content={
            store?.logo
              ? ogImage(store.logo, store.logoV2)
              : "https://dm2buy.blob.core.windows.net/dm2buy/favicon.ico"
          }
        />
        <meta property="og:site_name" content="dm2buy" />
        <meta
          property="og:url"
          content={typeof window !== "undefined" ? window.location.href : storeUrl}
        />

        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:url"
          content={typeof window !== "undefined" ? window.location.href : storeUrl}
        />
        <meta name="twitter:title" content={store?.storeName || "DM2Buy Store"} />
        <meta
          property="twitter:description"
          content={store?.storeBio || "Online store powered by DM2Buy"}
        />
        <meta
          property="twitter:image"
          content={
            "https:" + store?.logo || "https://dm2buy.blob.core.windows.net/dm2buy/favicon.ico"
          }
        />
      </Head>
      <MainHome store={store} theme={theme} />
    </div>
  );
}
